import type { RawSource } from '@pharmafiche/schema';
import { concatenateSources } from '@pharmafiche/schema';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

interface SourceStoreState {
  sources: RawSource[];
  addSource: (source: Omit<RawSource, 'id'> & { id?: string }) => RawSource;
  removeSource: (id: string) => void;
  updateSourceLabel: (id: string, label: string) => void;
  clear: () => void;
  totalChars: () => number;
  concatenated: () => string;
}

function makeId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `src-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export const useSourceStore = create<SourceStoreState>()(
  immer((set, get) => ({
    sources: [],

    addSource: (input) => {
      const next: RawSource = {
        id: input.id ?? makeId(),
        kind: input.kind,
        label: input.label,
        text: input.text,
        ...(input.page_range ? { page_range: input.page_range } : {}),
        ...(input.mime_type ? { mime_type: input.mime_type } : {}),
        ...(input.byte_size !== undefined ? { byte_size: input.byte_size } : {}),
      };
      set((s) => {
        s.sources.push(next);
      });
      return next;
    },

    removeSource: (id) => {
      set((s) => {
        s.sources = s.sources.filter((src) => src.id !== id);
      });
    },

    updateSourceLabel: (id, label) => {
      set((s) => {
        const target = s.sources.find((src) => src.id === id);
        if (target) target.label = label;
      });
    },

    clear: () => {
      set((s) => {
        s.sources = [];
      });
    },

    totalChars: () => {
      return get().sources.reduce((acc, s) => acc + s.text.length, 0);
    },

    concatenated: () => {
      return concatenateSources(get().sources);
    },
  })),
);
